import React, { useState, useRef, useEffect } from 'react';
import { FilterConfig } from '../../../types';

export interface FilterOption {
  label: string;
  value: any;
  disabled?: boolean;
  group?: string;
}

export interface FilterComponentProps {
  field: string;
  label: string;
  type: 'select' | 'multiselect' | 'text' | 'date' | 'daterange' | 'number' | 'numberrange' | 'boolean' | 'tags';
  options?: FilterOption[];
  value?: any;
  onChange: (filter: FilterConfig) => void;
  onClear?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  maxSelections?: number;
  minValue?: number;
  maxValue?: number;
  step?: number;
}

export function FilterComponent({
  field,
  label,
  type,
  options = [],
  value,
  onChange,
  onClear,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  clearable = true,
  searchable = false,
  maxSelections,
  minValue,
  maxValue,
  step = 1
}: FilterComponentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (newValue: any, operator: string = 'eq') => {
    onChange({
      field,
      operator: operator as any,
      value: newValue
    });
  };

  const handleClear = () => {
    handleChange(null);
    onClear?.();
  };

  const filteredOptions = searchable && searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const groupedOptions = filteredOptions.reduce((groups, option) => {
    const group = option.group || 'default';
    if (!groups[group]) groups[group] = [];
    groups[group].push(option);
    return groups;
  }, {} as Record<string, FilterOption[]>);

  // Common input classes
  const inputClasses = 'py-2 px-3 border border-neutral-300 dark:border-neutral-600 rounded text-sm bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 transition-all duration-200 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 focus:ring-2 focus:ring-primary-500/25 disabled:bg-neutral-100 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed';

  const clearButtonClasses = 'absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-neutral-500 dark:text-neutral-400 text-base p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10';

  const renderFilter = () => {
    switch (type) {
      case 'select':
        return (
          <div className="relative" ref={dropdownRef}>
            <select
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              className={`${inputClasses} w-full pr-8`}
              disabled={disabled}
              required={required}
            >
              <option value="">{placeholder || `Select ${label}`}</option>
              {Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
                groupName === 'default' ? (
                  groupOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </option>
                  ))
                ) : (
                  <optgroup key={groupName} label={groupName}>
                    {groupOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                )
              ))}
            </select>
            {clearable && value && (
              <button
                type="button"
                onClick={handleClear}
                className={clearButtonClasses}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        );

      case 'multiselect': {
        const selectedValues = Array.isArray(value) ? value : [];
        const canSelectMore = !maxSelections || selectedValues.length < maxSelections;

        return (
          <div className="relative" ref={dropdownRef}>
            <div
              className="min-h-[38px] py-1.5 px-3 pr-8 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 cursor-pointer flex items-center justify-between transition-colors duration-200 hover:border-primary-500 dark:hover:border-primary-400"
              onClick={() => !disabled && setIsOpen(!isOpen)}
            >
              <div className="flex flex-wrap gap-1 flex-1">
                {selectedValues.length === 0 ? (
                  <span className="text-neutral-400 dark:text-neutral-500 text-sm">{placeholder || `Select ${label}`}</span>
                ) : (
                  selectedValues.map((val, index) => {
                    const option = options.find(opt => opt.value === val);
                    return (
                      <span key={index} className="inline-flex items-center gap-1 py-0.5 px-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium">
                        {option?.label || val}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newValues = selectedValues.filter(v => v !== val);
                            handleChange(newValues, 'in');
                          }}
                          className="bg-transparent border-none cursor-pointer text-primary-700 dark:text-primary-300 text-[10px] p-0 leading-none rounded-full w-3.5 h-3.5 flex items-center justify-center hover:bg-primary-200/50 dark:hover:bg-primary-800/50"
                        >
                          ✕
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
              <span className="text-neutral-500 dark:text-neutral-400 text-xs ml-2">{isOpen ? '▲' : '▼'}</span>
            </div>

            {isOpen && (
              <div className="absolute top-full left-0 right-0 z-[1000] bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-t-0 rounded-b shadow-lg max-h-[200px] overflow-y-auto">
                {searchable && (
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full py-2 px-3 border-0 border-b border-neutral-200 dark:border-neutral-700 text-sm bg-transparent focus:outline-none focus:bg-neutral-50 dark:focus:bg-neutral-700"
                  />
                )}
                <div className="max-h-40 overflow-y-auto">
                  {filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    const isDisabled = option.disabled || (!canSelectMore && !isSelected);

                    return (
                      <label
                        key={option.value}
                        className={`flex items-center gap-2 py-2 px-3 cursor-pointer text-sm transition-colors duration-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={(e) => {
                            const newValues = e.target.checked
                              ? [...selectedValues, option.value]
                              : selectedValues.filter(v => v !== option.value);
                            handleChange(newValues, 'in');
                          }}
                          className="m-0"
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'text':
        return (
          <div className="relative">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleChange(e.target.value, 'contains')}
              placeholder={placeholder || `Filter by ${label}`}
              className={`${inputClasses} w-full ${clearable && value ? 'pr-8' : ''}`}
              disabled={disabled}
              required={required}
            />
            {clearable && value && (
              <button
                type="button"
                onClick={handleClear}
                className={clearButtonClasses}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        );

      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleChange(parseFloat(e.target.value) || null)}
              placeholder={placeholder || `Enter ${label}`}
              className={`${inputClasses} w-full ${clearable && value !== null && value !== undefined ? 'pr-8' : ''}`}
              disabled={disabled}
              required={required}
              min={minValue}
              max={maxValue}
              step={step}
            />
            {clearable && value !== null && value !== undefined && (
              <button
                type="button"
                onClick={handleClear}
                className={clearButtonClasses}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        );

      case 'numberrange':
        return (
          <div className="flex items-center gap-2 md:flex-col md:items-stretch">
            <input
              type="number"
              value={value?.min || ''}
              onChange={(e) => handleChange({
                ...value,
                min: parseFloat(e.target.value) || null
              }, 'range')}
              placeholder="Min"
              className={`${inputClasses} flex-1`}
              disabled={disabled}
              min={minValue}
              max={maxValue}
              step={step}
            />
            <span className="text-neutral-500 dark:text-neutral-400 text-sm whitespace-nowrap md:text-center">to</span>
            <input
              type="number"
              value={value?.max || ''}
              onChange={(e) => handleChange({
                ...value,
                max: parseFloat(e.target.value) || null
              }, 'range')}
              placeholder="Max"
              className={`${inputClasses} flex-1`}
              disabled={disabled}
              min={minValue}
              max={maxValue}
              step={step}
            />
          </div>
        );

      case 'date':
        return (
          <div className="relative">
            <input
              type="date"
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              className={`${inputClasses} w-full ${clearable && value ? 'pr-8' : ''}`}
              disabled={disabled}
              required={required}
            />
            {clearable && value && (
              <button
                type="button"
                onClick={handleClear}
                className={clearButtonClasses}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        );

      case 'daterange':
        return (
          <div className="flex items-center gap-2 md:flex-col md:items-stretch">
            <input
              type="date"
              value={value?.start || ''}
              onChange={(e) => handleChange({
                ...value,
                start: e.target.value
              }, 'range')}
              placeholder="Start date"
              className={`${inputClasses} flex-1`}
              disabled={disabled}
            />
            <span className="text-neutral-500 dark:text-neutral-400 text-sm whitespace-nowrap md:text-center">to</span>
            <input
              type="date"
              value={value?.end || ''}
              onChange={(e) => handleChange({
                ...value,
                end: e.target.value
              }, 'range')}
              placeholder="End date"
              className={`${inputClasses} flex-1`}
              disabled={disabled}
            />
          </div>
        );

      case 'boolean':
        return (
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer text-sm m-0">
              <input
                type="checkbox"
                checked={value === true}
                onChange={(e) => handleChange(e.target.checked)}
                className="m-0"
                disabled={disabled}
              />
              <span className="text-neutral-700 dark:text-neutral-300">{placeholder || label}</span>
            </label>
          </div>
        );

      case 'tags': {
        const tags = Array.isArray(value) ? value : [];
        return (
          <div className="border border-neutral-300 dark:border-neutral-600 rounded p-1.5 bg-white dark:bg-neutral-800 min-h-[38px]">
            <div className="flex flex-wrap gap-1 items-center">
              {tags.map((tag, index) => (
                <span key={index} className="inline-flex items-center gap-1 py-1 px-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium">
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      const newTags = tags.filter((_, i) => i !== index);
                      handleChange(newTags, 'in');
                    }}
                    className="bg-transparent border-none cursor-pointer text-primary-700 dark:text-primary-300 text-[10px] p-0 leading-none rounded-full w-3.5 h-3.5 flex items-center justify-center hover:bg-primary-200/50 dark:hover:bg-primary-800/50"
                  >
                    ✕
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) {
                    e.preventDefault();
                    const newTags = [...tags, tagInput.trim()];
                    handleChange(newTags, 'in');
                    setTagInput('');
                  }
                }}
                placeholder={placeholder || `Add ${label}`}
                className="border-none outline-none p-1 text-sm flex-1 min-w-[100px] bg-transparent text-neutral-800 dark:text-neutral-200"
                disabled={disabled}
              />
            </div>
          </div>
        );
      }

      default:
        return <div className="text-danger-500 text-sm py-2 px-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded">Unsupported filter type: {type}</div>;
    }
  };

  return (
    <div className={`flex flex-col gap-1.5 min-w-[200px] md:min-w-0 ${disabled ? 'opacity-60 pointer-events-none' : ''} ${className}`}>
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 m-0">
        {label}
        {required && <span className="text-danger-500 ml-1">*</span>}
      </label>
      {renderFilter()}
    </div>
  );
}